import { useState } from 'react';
import { Mail, MailCheck, AlertCircle, ArrowLeft } from 'lucide-react';
import Reveal from '../components/animations/Reveal';
import AuthBackground from '../components/animations/AuthBackground';
import { forgotPassword } from '../api/authApi';

const INPUT_CLASS =
  'input-anim w-full bg-white/95 border border-white/40 rounded-xl p-3 pl-11 text-slate-800 placeholder-slate-400 focus:border-brand focus:ring-2 focus:ring-brand/30 focus:outline-none transition-all';

const ForgotPasswordPage = ({ setActiveTab }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await forgotPassword({ email: email.trim() });
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-4rem)] flex flex-col items-center pt-16 pb-16 px-4">
      <AuthBackground />
      <div className="relative max-w-md w-full mx-auto sm:px-6 lg:px-8">
        <Reveal>
          <div className="text-center mb-6">
            <h2 className="font-display text-4xl font-bold text-white mb-3 drop-shadow-sm">Reset Password</h2>
            <p className="text-xl text-white/80">We'll help you get back into your account</p>
          </div>
        </Reveal>

        <Reveal delay={100}>
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
            {submitted ? (
              <div className="text-center py-4">
                <MailCheck className="w-10 h-10 text-white/80 mx-auto mb-4" />
                <p className="text-white mb-2 font-medium">Check your email</p>
                <p className="text-sm text-white/60 mb-6">
                  If an account exists for <span className="text-white/90">{email}</span>, a password reset
                  link is on its way. It's valid for 1 hour — check Spam if it doesn't show up shortly.
                </p>
                <button
                  type="button"
                  onClick={() => setActiveTab?.('login')}
                  className="text-sm text-white/60 hover:text-white transition-colors flex items-center gap-2 mx-auto"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </button>
              </div>
            ) : (
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div className="relative">
                  <Mail className="w-4.5 h-4.5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={INPUT_CLASS}
                    placeholder="Enter the email on your account"
                  />
                </div>

                {error && (
                  <div className="flex items-start space-x-3 bg-red-500/10 border border-red-400/30 rounded-2xl p-4">
                    <AlertCircle className="w-5 h-5 text-red-300 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-200">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="brand-btn w-full px-6 py-3 rounded-xl font-semibold hover:scale-[1.02] transition-transform duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Reset Link'}
                </button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setActiveTab?.('login')}
                    className="text-white/70 hover:text-white text-sm transition-colors flex items-center gap-2 mx-auto"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Login
                  </button>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;